"use client";

import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
} from "@/components/tool-kit/styles";
import type { HANGMAN_GAME_STRINGS } from "@/components/tools/hangman-game";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  MAX_ANSWER_LETTERS,
  MAX_MISSES,
  MAX_TEAMS,
  MAX_WORDS,
  MIN_MISSES,
  type ParsedHangman,
} from "@/lib/tool-kit/games-organizers/hangman";

type Strings = typeof HANGMAN_GAME_STRINGS;

const MISS_CHOICES = Array.from(
  { length: MAX_MISSES - MIN_MISSES + 1 },
  (_, i) => MIN_MISSES + i
);
const TEAM_CHOICES = Array.from({ length: MAX_TEAMS - 1 }, (_, i) => i + 2);

export interface HangmanSettings {
  text: string;
  misses: number;
  teams: number;
  shuffle: boolean;
  clues: boolean;
  remember: boolean;
}

/** The word list and the options, before the game starts. */
export default function HangmanSetup({
  settings,
  parsed,
  error,
  strings,
  onChange,
  onRemember,
  onStart,
  onClear,
}: {
  settings: HangmanSettings;
  parsed: ParsedHangman;
  error: boolean;
  strings: Strings;
  onChange: (patch: Partial<HangmanSettings>) => void;
  onRemember: (on: boolean) => void;
  onStart: () => void;
  onClear: () => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const skipped = parsed.rejected.map((item) => item.line);
  return (
    <div className="space-y-4">
      <TextAreaField
        hint={
          <>
            {t(strings.wordsHint, {
              max: MAX_WORDS,
              maxLetters: MAX_ANSWER_LETTERS,
            })}{" "}
            {tPlural(strings.wordCount, locale, parsed.entries.length, {
              count: fmt(parsed.entries.length),
            })}
          </>
        }
        id="hangman-words"
        label={strings.wordsLabel}
        onChange={(text) => onChange({ text })}
        placeholder={strings.wordsPlaceholder}
        prose
        value={settings.text}
      />
      <button
        className={BUTTON_SMALL}
        onClick={() => onChange({ text: strings.example })}
        type="button"
      >
        {strings.loadExample}
      </button>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="hangman-misses"
          label={strings.missesLabel}
          onChange={(value) => onChange({ misses: Number(value) })}
          options={MISS_CHOICES.map((n) => ({
            value: String(n),
            label: tPlural(strings.missesOption, locale, n, { count: fmt(n) }),
          }))}
          value={String(settings.misses)}
        />
        <SelectField
          hint={strings.teamsHint}
          id="hangman-teams"
          label={strings.teamsLabel}
          onChange={(value) => onChange({ teams: Number(value) })}
          options={[
            { value: "0", label: strings.noTeams },
            ...TEAM_CHOICES.map((n) => ({
              value: String(n),
              label: tPlural(strings.teamsOption, locale, n, { count: fmt(n) }),
            })),
          ]}
          value={String(settings.teams)}
        />
      </div>
      <fieldset className="space-y-2">
        <Checkbox
          checked={settings.shuffle}
          label={strings.shuffle}
          onChange={(shuffle) => onChange({ shuffle })}
        />
        <Checkbox
          checked={settings.clues}
          label={strings.showClues}
          onChange={(clues) => onChange({ clues })}
        />
        <Checkbox
          checked={settings.remember}
          label={strings.remember}
          onChange={onRemember}
        />
      </fieldset>
      <p className={HINT}>{strings.rememberNote}</p>
      {skipped.length > 0 ? (
        <Notice>{t(strings.skipped, { lines: skipped.join(", ") })}</Notice>
      ) : null}
      {parsed.duplicates > 0 ? (
        <Notice>{t(strings.repeats, { count: fmt(parsed.duplicates) })}</Notice>
      ) : null}
      {parsed.overflow > 0 ? (
        <Notice>{t(strings.overflow, { max: MAX_WORDS })}</Notice>
      ) : null}
      {error ? <ErrorNotice>{strings.needWords}</ErrorNotice> : null}
      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_PRIMARY} onClick={onStart} type="button">
          {strings.start}
        </button>
        <button className={BUTTON_SECONDARY} onClick={onClear} type="button">
          {strings.clearSaved}
        </button>
      </div>
    </div>
  );
}
